'use client';

import { createContext, useContext, useState } from 'react';

/**
 * Ark UI only keeps an overlay mounted while a *CSS* exit animation runs. Motion animates
 * with JS, so we take over Ark's `present` flag: the overlay stays present after it closes
 * until the Motion exit animation reports that it's done.
 */

interface OpenChangeDetails {
  open: boolean;
}

interface Options<D extends OpenChangeDetails> {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (details: D) => void;
}

export interface MotionPresence<D extends OpenChangeDetails = OpenChangeDetails> {
  /** Whether the overlay is (or is about to be) open. */
  open: boolean;
  /** Whether it should be in the DOM: open, or still animating out. */
  present: boolean;
  onOpenChange: (details: D) => void;
  /** Call when the exit animation finishes. */
  onExitComplete: () => void;
}

export function useMotionPresence<D extends OpenChangeDetails>({
  open: openProp,
  defaultOpen = false,
  onOpenChange,
}: Options<D>): MotionPresence<D> {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = openProp ?? uncontrolledOpen;
  const [present, setPresent] = useState(open);

  // Opening mounts straight away; closing waits for onExitComplete.
  if (open && !present) setPresent(true);

  return {
    open,
    present,
    onOpenChange: (details) => {
      if (openProp === undefined) setUncontrolledOpen(details.open);
      onOpenChange?.(details);
    },
    onExitComplete: () => {
      if (!open) setPresent(false);
    },
  };
}

const MotionPresenceContext = createContext<MotionPresence | null>(null);

export const MotionPresenceProvider = MotionPresenceContext.Provider;

export function useMotionPresenceContext(): MotionPresence {
  const context = useContext(MotionPresenceContext);
  if (!context) throw new Error('Animated overlay parts must be rendered inside their Root');
  return context;
}

/** onAnimationComplete handler that reports the end of the `closed` variant. */
export const onClosed = (done: () => void) => (definition: unknown) => {
  if (definition === 'closed') done();
};
